<script lang="ts">
const __DsBlock = {
	icon: 'lucide:image',
	description: 'Displays an image.',
	category: 'media',
	hidden: true,
	options: [
		{
			name: 'url',
		},
		{
			name: 'alt',
		},
		{
			name: 'rounded',
		},
	],
}
</script>
<script lang="ts" setup>
import { useElementSize } from '@vueuse/core';
import { decode, encode } from 'blurhash';
import { computed, reactive, ref, useTemplateRef, watch, watchEffect } from 'vue';
import { defineBlock, useBlockInEditor, useEditorBlockProp } from '#layers/block-engine';

// TODO upload image

const props = withDefaults(defineProps<{
	/**
	 * URL of the image.
	 */
	url?: string;

	alt?: string;

	/**
	 * Whether the image should have rounded corners.
	 */
	rounded?: boolean;

	/**
	 * Internal data saved on the block.
	 */
	imageData?: {
		blurHash?: string;
		blurHashWidth?: number;
		blurHashHeight?: number;
		width?: number;
		height?: number;
	};
}>(), {
	url: '/image-placeholder.png',
	rounded: true,
});

;

const blurHashMaxSize = 20;

const el = useTemplateRef('el');
const { width } = useElementSize(el);

const size = reactive({
	width: props.imageData?.width ?? 1,
	height: props.imageData?.height ?? 1,
});

const loaded = ref(false);

const inEditor = useBlockInEditor();

watch(() => props.url, (url) => {
	if (!url.trim()) {
		return;
	}

	loaded.value = false;
}, {
	immediate: true,
});

const resizedSize = computed(() => {
	const maxWidth = width.value;
	const ratio = size.width / size.height;
	const newWidth = Math.min(maxWidth, size.width);
	const newHeight = newWidth / ratio;
	return {
		width: Math.round(newWidth),
		height: Math.round(newHeight),
	};
});

// BlurHash preview

const canvas = useTemplateRef('canvas');

watchEffect(() => {
	if (canvas.value && props.imageData?.blurHash && props.imageData.blurHashWidth && props.imageData.blurHashHeight) {
		const ctx = canvas.value.getContext('2d');

		if (ctx) {
			const width = canvas.value.width = props.imageData.blurHashWidth;
			const height = canvas.value.height = props.imageData.blurHashHeight;

			const pixels = decode(props.imageData.blurHash, width, height);
			const imageData = ctx.createImageData(width, height);
			imageData.data.set(pixels);
			ctx.putImageData(imageData, 0, 0);
		}
	}
});

// Process image

const imageDataProp = inEditor ? useEditorBlockProp<typeof props.imageData>('imageData') : null;

function onLoad(event: Event) {
	const image = event.currentTarget as HTMLImageElement;

	size.width = image.width;
	size.height = image.height;
	loaded.value = true;

	if (inEditor) {
		let blurHash: string | undefined;

		let drawWidth;
		let drawHeight;

		if (image.width > image.height) {
			drawWidth = blurHashMaxSize;
			drawHeight = Math.round(blurHashMaxSize * (image.height / image.width));
		}
		else {
			drawHeight = blurHashMaxSize;
			drawWidth = Math.round(blurHashMaxSize * (image.width / image.height));
		}

		try {
			const canvas = document.createElement('canvas');
			canvas.width = drawWidth;
			canvas.height = drawHeight;
			const ctx = canvas.getContext('2d');

			if (ctx) {
				ctx.drawImage(image, 0, 0, drawWidth, drawHeight);
				const imageData = ctx.getImageData(0, 0, drawWidth, drawHeight);
				blurHash = encode(imageData.data, drawWidth, drawHeight, 4, 4);
			}

			imageDataProp!.value = {
				blurHash,
				blurHashWidth: drawWidth,
				blurHashHeight: drawHeight,
				width: image.width,
				height: image.height,
			};
		}
		catch (error) {
			console.warn('Error generating blur hash:', error);

			imageDataProp!.value = {
				width: image.width,
				height: image.height,
			};
		}
	}
}
</script>

<template>
	<div class="shrink">
		<div
			ref="el"
			class="relative"
			:style="!loaded ? {
				width: resizedSize.width ? `${resizedSize.width}px` : 'auto',
				height: resizedSize.height ? `${resizedSize.height}px` : 'auto',
			} : {}"
		>
			<canvas
				v-if="!loaded && imageData?.blurHash"
				ref="canvas"
				class="absolute inset-0 size-full"
				:class="{
					'rounded-md': rounded,
				}"
			/>
			<NuxtImg
				v-show="loaded"
				:src="url"
				:alt
				:class="{
					'rounded-md': rounded,
				}"
				@load="onLoad"
			/>
		</div>
	</div>
</template>
